import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { Page, LabelKey } from '@longnecktech/splash-commons-fe';
import { NotificationService } from '@services/notification.service';
import { BaseEntity } from '@shared/types/base-association';
import { GetRequestParams } from '@shared/types/request-params';
import {
  SegmentConfiguration,
  SegmentsPrizeAssociations,
  WheelSegment,
  TextAssociations,
  WheelSegmentType,
} from '@shared/types/wheel-segment';
import { createParamsWithPagination } from '@shared/utils/http.util';
import { BehaviorSubject, catchError, throwError, Observable } from 'rxjs';
import { tap, map, finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WheelSegmentsService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private _segmentsSummary = new BehaviorSubject<BaseEntity[]>([]);
  private _segmentsSummaryInner = new BehaviorSubject<BaseEntity[]>([]);
  private _segmentsSummaryOuter = new BehaviorSubject<BaseEntity[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _prizeAssociations = new BehaviorSubject<
    Record<string, SegmentsPrizeAssociations[]>
  >({});

  readonly segmentsSummary$ = this._segmentsSummary.asObservable();
  readonly segmentsSummaryInner$ = this._segmentsSummaryInner.asObservable();
  readonly segmentsSummaryOuter$ = this._segmentsSummaryOuter.asObservable();
  readonly prizeAssociations$ = this._prizeAssociations.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  constructor() {}

  fetchConfigsSummary(type?: WheelSegmentType): Observable<BaseEntity[]> {
    if (!type && this._segmentsSummary.value.length) {
      return this.segmentsSummary$;
    }

    if (type === 'INNER' && this._segmentsSummaryInner.value.length) {
      return this.segmentsSummaryInner$;
    }

    if (type === 'OUTER' && this._segmentsSummaryOuter.value.length) {
      return this.segmentsSummaryOuter$;
    }

    this._isLoading.next(true);

    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }

    return this.http
      .get<
        BaseEntity[]
      >(environment.backendUrl + `/api/v3/admin/wheel-segment/all/summary`, { params })
      .pipe(
        tap((res) => {
          this._isLoading.next(false);

          if (!type) {
            this._segmentsSummary.next(res);
            return;
          }

          if (type === 'INNER') {
            this._segmentsSummaryInner.next(res);
            return;
          }

          if (type === 'OUTER') {
            this._segmentsSummaryOuter.next(res);
          }
        }),
        catchError((err) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchConfigs(
    params: GetRequestParams,
    isInner: boolean = false,
  ): Observable<Page<SegmentConfiguration>> {
    this._isLoading.next(true);
    let httpParams = createParamsWithPagination(params);
    httpParams = httpParams.set('isInner', isInner.toString());
    return this.http
      .get<
        Page<SegmentConfiguration>
      >(environment.backendUrl + `/api/v3/admin/wheel-segment/all`, { params: httpParams })
      .pipe(
        map((res) => {
          if (res.content.length) {
            return {
              ...res,
              content: res.content.map((config) => ({
                ...config,
                wheelSegments: config.wheelSegments.map((segment) => ({
                  ...segment,
                  text: undefined as unknown as LabelKey,
                  textAssociations: this.getTextAssociations(segment),
                })),
              })),
            };
          }
          return res;
        }),
        tap(() => {
          this._isLoading.next(false);
        }),
        catchError((err) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  fetchSegmentsPrizeAssociations(
    configUuid: string,
  ): Observable<SegmentsPrizeAssociations[]> {
    this._isLoading.next(true);
    return this.http
      .get<
        SegmentsPrizeAssociations[]
      >(environment.backendUrl + `/api/v3/admin/wheel-segment/${configUuid}/prize-associations`)
      .pipe(
        map((res) => {
          // let's save the associations in the state
          // for each configuration
          this._prizeAssociations.next({
            ...this._prizeAssociations.getValue(),
            [configUuid]: res || [],
          });
          return res;
        }),
        finalize(() => this._isLoading.next(false)),
      );
  }

  createConfig(
    segment: SegmentConfiguration,
  ): Observable<SegmentConfiguration> {
    this._isLoading.next(true);
    return this.http
      .post<SegmentConfiguration>(
        environment.backendUrl + `/api/v3/admin/wheel-segment`,
        segment,
      )
      .pipe(
        tap(() => this._isLoading.next(false)),
        catchError((error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification(
            'segment.segmentCreateError',
          );
          return throwError(() => error);
        }),
      );
  }

  updateConfig(config: SegmentConfiguration): Observable<SegmentConfiguration> {
    this._isLoading.next(true);
    return this.http
      .put<SegmentConfiguration>(
        environment.backendUrl + `/api/v3/admin/wheel-segment/${config.uuid}`,
        config,
      )
      .pipe(
        tap(() => {
          this._isLoading.next(false);
          // reset prize associations because they could be changed
          this._prizeAssociations.next({
            ...this._prizeAssociations.getValue(),
            [config.uuid]: [],
          });
        }),
        catchError((error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification(
            'segment.errorUpdatingSegments',
          );
          return throwError(() => error);
        }),
      );
  }

  deleteConfig(uuid: string): Observable<string> {
    this._isLoading.next(true);
    return this.http
      .delete<string>(
        environment.backendUrl + `/api/admin/wheel-segment/${uuid}`,
      )
      .pipe(
        tap(() => this._isLoading.next(false)),
        catchError((error) => {
          this._isLoading.next(false);
          this.notificationService.showNotification(
            'segment.segmentDeleteError',
          );
          return throwError(() => error);
        }),
      );
  }

  resetData(): void {
    this._segmentsSummary.next([]);
    this._segmentsSummaryInner.next([]);
    this._segmentsSummaryOuter.next([]);
  }
  private getTextAssociations(segment: WheelSegment): TextAssociations[] {
    // if there are no text associations, we use text label as text association
    // otherwise, just return text associations
    // we need this to support new backend
    if (segment.text && !segment.textAssociations.length) {
      return [
        {
          uuid: undefined,
          text: segment.text.enValue!,
          priority: 1,
          tags: [],
          countries: [],
          brands: [],
          currencies: [],
        },
      ];
    }
    return segment.textAssociations;
  }

  fetchConfigByUuid(uuid: string): Observable<SegmentConfiguration> {
    return this.http.get<SegmentConfiguration>(
      `${environment.backendUrl}/api/v3/admin/wheel-segment/${uuid}`,
    );
  }
}
